import { X, Zap, Bell, BarChart3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: "comparison" | "alerts" | "history";
  currentLimit: number;
  onUpgrade?: () => void;
}

const CONTENT = {
  comparison: {
    icon: Zap,
    headline: "You Found the Third Protocol",
    subheadline: "That is exactly the moment serious stakers upgrade.",
    body: "Free accounts compare 2 protocols. You just found a third worth evaluating — which means you are doing exactly what ResidualVault was built for. Starter gives you 5 simultaneous comparisons, full 48-point APY history, and unlimited alerts for $4.99 per month.",
    cta: "Upgrade to Starter — $4.99/mo",
  },
  alerts: {
    icon: Bell,
    headline: "Your Second Alert Would Have Caught It",
    subheadline: "Most yield opportunities are gone within 72 hours.",
    body: "You set your first alert. Free accounts stop at one. The stakers who catch the best yield windows set alerts on every protocol they are watching — not just one. Unlimited alerts are included in every paid plan.",
    cta: "Upgrade to Starter — $4.99/mo",
  },
  history: {
    icon: BarChart3,
    headline: "The Pattern Is in the Last 36 Data Points",
    subheadline: "You are looking at 12. The full story needs 48.",
    body: "APY spikes, compression cycles, and incentive program signatures only become visible in the full 48-point history. Free accounts see the last 12. Starter shows everything.",
    cta: "Upgrade to Starter — $4.99/mo",
  },
};

export default function UpgradeModal({ isOpen, onClose, feature, currentLimit: _currentLimit, onUpgrade }: UpgradeModalProps) {
  void _currentLimit;
  const content = CONTENT[feature];
  const Icon = content.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-navy-dark border border-sky-blue/20 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            <div className="relative bg-gradient-to-br from-sky-blue/10 to-transparent p-8 pb-6">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/40 hover:text-white transition"
              >
                <X size={20} />
              </button>

              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-sky-blue/20 border border-sky-blue/30 mb-5">
                <Icon size={28} className="text-sky-blue" />
              </div>

              <h2 className="text-2xl font-bold text-white mb-2">
                {content.headline}
              </h2>
              <p className="text-sky-blue font-medium text-sm">
                {content.subheadline}
              </p>
            </div>

            <div className="px-8 py-6">
              <p className="text-white/70 leading-relaxed text-sm">
                {content.body}
              </p>
            </div>

            <div className="px-8 pb-8 space-y-3">
              <Link
                to={`/pricing?plan=starter&source=upgrade_modal_${feature}`}
                onClick={() => { onUpgrade?.(); onClose(); }}
                className="block w-full text-center py-3.5 px-6 bg-sky-blue hover:bg-sky-blue/90 text-navy-dark font-bold rounded-xl transition text-base"
              >
                {content.cta}
              </Link>
              <Link
                to="/pricing"
                onClick={onClose}
                className="block w-full text-center py-2.5 px-6 text-white/50 hover:text-white/80 font-medium text-sm transition"
              >
                See all plan features
              </Link>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
